import { defineAsyncComponent, defineComponent } from 'vue';
import * as constants from '@/common/constants';
const AdminHome = defineAsyncComponent(() => import(/* webpackChunkName: "admin" */ '@/client/components/admin/AdminHome.vue'));
const CardList = defineAsyncComponent(() => import(/* webpackChunkName: "card-list" */ '@/client/components/cardlist/CardList.vue'));
const CreateGameForm = defineAsyncComponent(() => import(/* webpackChunkName: "create-game" */ '@/client/components/create/CreateGameForm.vue'));
const GameEnd = defineAsyncComponent(() => import(/* webpackChunkName: "game-end" */ '@/client/components/GameEnd.vue'));
const GameHome = defineAsyncComponent(() => import(/* webpackChunkName: "game-home" */ '@/client/components/GameHome.vue'));
const GamesOverview = defineAsyncComponent(() => import(/* webpackChunkName: "games-overview" */ '@/client/components/GamesOverview.vue'));
const Help = defineAsyncComponent(() => import(/* webpackChunkName: "help" */ '@/client/components/help/Help.vue'));
const LoginHome = defineAsyncComponent(() => import(/* webpackChunkName: "login" */ '@/client/components/auth/LoginHome.vue'));
const LoadGameForm = defineAsyncComponent(() => import(/* webpackChunkName: "load-game" */ '@/client/components/LoadGameForm.vue'));
const PlayerHome = defineAsyncComponent(() => import(/* webpackChunkName: "player-home" */ '@/client/components/PlayerHome.vue'));
const SpectatorHome = defineAsyncComponent(() => import(/* webpackChunkName: "spectator-home" */ '@/client/components/SpectatorHome.vue'));
const SetupTable = defineAsyncComponent(() => import(/* webpackChunkName: "setup-table" */ '@/client/components/lobby/SetupTable.vue'));
const AccountLogin = defineAsyncComponent(() => import(/* webpackChunkName: "account" */ '@/client/components/account/AccountLogin.vue'));
const MyGames = defineAsyncComponent(() => import(/* webpackChunkName: "account" */ '@/client/components/account/MyGames.vue'));
import AccountTag from '@/client/components/account/AccountTag.vue';
import UiScaleControl from '@/client/components/UiScaleControl.vue';
import { rememberGame } from '@/client/utils/account';
const JoinRoom = defineAsyncComponent(() => import(/* webpackChunkName: "join-room" */ '@/client/components/lobby/JoinRoom.vue'));
const StartScreen = defineAsyncComponent(() => import(/* webpackChunkName: "start-screen" */ '@/client/components/StartScreen.vue'));
import { setTranslationContext, translateServerError } from '@/client/directives/i18n';
import { alertDialog } from '@/client/utils/dialogs';
import { paths } from '@/common/app/paths';
import { isPlayerId, isSpectatorId } from '@/common/Types';
import { setDocumentTitle } from '../utils/documentTitle';
import { SoundManager } from '@/client/utils/SoundManager';
/** Games this page already noted in "My games" and the recent games: once per page load is enough. */
const remembered = new Set();
function rememberOnce(id) {
    if (!remembered.has(id)) {
        remembered.add(id);
        rememberGame(id);
    }
}
// NOTE: this simplistic truncation to the last segment might cause issues if
// this page starts supporting paths more than one level deep.
function getLastPathSegment() {
    // Leave only the last part of /path
    return window.location.pathname.replace(/.*\//g, '');
}
export default defineComponent({
    name: 'App',
    data() {
        return {
            screen: 'empty',
            isServerSideRequestInProgress: false,
            componentsVisibility: {
                'milestones': true,
                'awards_list': true,
                'tags_concise': false,
                'pinned_player_0': false,
                'pinned_player_1': false,
                'pinned_player_2': false,
                'pinned_player_3': false,
                'pinned_player_4': false,
                'turmoil_parties': false,
            },
            game: undefined,
            playerView: undefined,
            spectator: undefined,
            lobby: undefined,
            login: undefined,
        };
    },
    components: {
        StartScreen,
        CreateGameForm,
        LoadGameForm,
        GameHome,
        PlayerHome,
        SpectatorHome,
        SetupTable,
        GameEnd,
        GamesOverview,
        CardList,
        Help,
        AdminHome,
        LoginHome,
        AccountLogin,
        MyGames,
        AccountTag,
        UiScaleControl,
        JoinRoom,
    },
    computed: {
        participant() {
            return this.playerView ?? this.spectator;
        },
    },
    methods: {
        /**
         * Shows an error in the game's dialog. `message` is often a server error in English; it's
         * translated, placeholders and all (see translateServerMessage). `cb` runs once it's closed.
         */
        showAlert(title, message, cb = () => { }) {
            SoundManager.play('error');
            alertDialog({ title, message: translateServerError(message), translated: true }).then(cb);
        },
        setVisibilityState(targetVar, isVisible) {
            if (isVisible === this.getVisibilityState(targetVar)) {
                return;
            }
            this.componentsVisibility[targetVar] = isVisible;
        },
        getVisibilityState(targetVar) {
            return this.componentsVisibility[targetVar] ? true : false;
        },
        update(path) {
            const currentPathname = getLastPathSegment();
            const app = this;
            const url = 'api/' + path + window.location.search.replace('&noredirect', '');
            fetch(url)
                .then(async (resp) => {
                if (resp.status === 404) {
                    // Before the game starts, player and spectator links show the setup table.
                    const lobbyResponse = await fetch(paths.API_LOBBY + window.location.search.replace('&noredirect', ''));
                    if (lobbyResponse.ok) {
                        const lobby = await lobbyResponse.json();
                        if (!lobby.started) {
                            app.lobby = lobby;
                            app.screen = 'setup-table';
                            rememberOnce(lobby.participantId);
                            return undefined;
                        }
                        // Started in the meantime: ask for the game again.
                        resp = await fetch(url);
                    }
                }
                if (!resp.ok) {
                    throw new Error(`Error getting game data: ${resp.statusText}`);
                }
                return resp.json();
            })
                .then((model) => {
                if (model === undefined) {
                    return;
                }
                rememberOnce(model.id);
                if (path === paths.PLAYER) {
                    app.playerView = model;
                    setTranslationContext(app.playerView);
                }
                else if (path === paths.SPECTATOR) {
                    app.spectator = model;
                }
                if (model.game.phase === 'end' &&
                    window.location.search.includes('&noredirect') === false) {
                    app.screen = 'the-end';
                    if (currentPathname !== paths.THE_END) {
                        window.history.replaceState(model, `${constants.APP_NAME} - Player`, `${paths.THE_END}?id=${model.id}`);
                    }
                }
                else {
                    if (path === paths.PLAYER) {
                        app.screen = 'player-home';
                    }
                    else if (path === paths.SPECTATOR) {
                        app.screen = 'spectator-home';
                    }
                    if (currentPathname !== path) {
                        window.history.replaceState(model, `${constants.APP_NAME} - Game`, `${path}?id=${model.id}`);
                    }
                }
            })
                .catch((err) => {
                alertDialog({ title: 'Error', message: 'Error getting game data' });
                console.error(err);
            });
        },
        updatePlayer() {
            this.update(paths.PLAYER);
        },
        updateSpectator() {
            this.update(paths.SPECTATOR);
        },
    },
    mounted() {
        setDocumentTitle();
        const currentPathname = getLastPathSegment();
        const app = this;
        if (currentPathname === paths.PLAYER) {
            app.updatePlayer();
        }
        else if (currentPathname === paths.THE_END) {
            const urlParams = new URLSearchParams(window.location.search);
            const id = urlParams.get('id') || '';
            if (isPlayerId(id)) {
                app.updatePlayer();
            }
            else if (isSpectatorId(id)) {
                app.updateSpectator();
            }
            else {
                alertDialog({ title: 'Error', message: 'Bad id URL parameter.' });
            }
        }
        else if (currentPathname === paths.GAME) {
            const url = paths.API_GAME + window.location.search;
            fetch(url)
                .then((resp) => {
                if (!resp.ok) {
                    throw new Error(`Error getting game data: ${resp.statusText}`);
                }
                return resp.json();
            })
                .then((appGame) => {
                rememberOnce(appGame.id);
                app.screen = 'game-home';
                app.game = appGame;
                window.history.replaceState(appGame, `${constants.APP_NAME} - Game`, `${paths.GAME}?id=${appGame.id}`);
            })
                .catch((err) => {
                alertDialog({ title: 'Error', message: 'Error getting game data' });
                console.error(err);
            });
        }
        else if (currentPathname === paths.GAMES_OVERVIEW) {
            app.screen = 'games-overview';
        }
        else if (currentPathname === paths.NEW_GAME) {
            app.screen = 'create-game-form';
        }
        else if (currentPathname === paths.LOAD) {
            app.screen = 'load';
        }
        else if (currentPathname === paths.CARDS) {
            app.screen = 'cards';
        }
        else if (currentPathname === paths.HELP) {
            app.screen = 'help';
        }
        else if (currentPathname === paths.SPECTATOR) {
            app.updateSpectator();
        }
        else if (currentPathname === paths.ADMIN) {
            app.screen = 'admin';
        }
        else if (currentPathname === paths.JOIN) {
            app.screen = 'join';
        }
        else if (currentPathname === paths.LOGIN) {
            app.screen = 'login-home';
        }
        else if (currentPathname === paths.ACCOUNT) {
            app.screen = 'account';
        }
        else if (currentPathname === paths.MY_GAMES) {
            app.screen = 'my-games';
        }
        else {
            app.screen = 'start-screen';
        }
    },
});
